import React, { useEffect, useMemo, useState } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { validateImage } from "../../../utils/imageHelper";
import StyleControls from "./StyleControls";

/**
 * Img_circle
 * - Shows a centered circular image.
 * - Supports API payloads that send `image_placeholder` instead of `image`.
 * - Optionally shows `description` below the circle (your API sends this).
 */
const ImgCircle = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  // In preview: use data/props; in edit mode: use context state
  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    description = "",
    image = null,
    image_placeholder = null,
    background_color = "#ffffff",
    backgroundColor,
    component_name = "Img_circle",
  } = sectionData;

  const resolvedBackground = useMemo(
    () => backgroundColor || background_color || "#ffffff",
    [backgroundColor, background_color]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [localDescription, setLocalDescription] = useState(description || "");
  const [localImage, setLocalImage] = useState(validateImage(image) || validateImage(image_placeholder));
  const [localBg, setLocalBg] = useState(resolvedBackground);
  const [imageUrl, setImageUrl] = useState(null);

  // Sync local state when section id changes (section loaded/changed)
  useEffect(() => {
    setLocalDescription(description || "");
    setLocalImage(validateImage(image) || validateImage(image_placeholder));
    setLocalBg(resolvedBackground);
  }, [id]); // Only sync when section id changes, not on every prop change

  // Create URL for File objects
  useEffect(() => {
    if (localImage instanceof File || localImage instanceof Blob) {
      const url = URL.createObjectURL(localImage);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (typeof localImage === "string") {
      setImageUrl(localImage);
      return;
    }
    setImageUrl(null);
  }, [localImage]);

  // Merge section-specific styles with parent styles
  const sectionStyleSettings = sectionData?.styleSettings || {};
  const effectiveStyleSettings = {
    ...styleSettings,  // Parent styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };
  const {
    fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif",
    backgroundColor: styleBackgroundColor = "#ffffff"
  } = effectiveStyleSettings;

  const handleSave = () => {
    const currentSection = sections.find(section => section.id === id) || {};
    updateSection(id, {
      id,
      component_name,
      description: localDescription,
      image: localImage,
      image_placeholder: image_placeholder || null,
      background_color: localBg,
      backgroundColor: localBg,
      styleSettings: {
        ...(currentSection.styleSettings || {}) // Preserve existing styleSettings
      }
    });
    setIsEditing(false);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Circular Image</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Background Color</label>
          <div className="d-flex gap-2 align-items-center">
            <input
              type="color"
              className="form-control form-control-color"
              value={localBg}
              onChange={(e) => setLocalBg(e.target.value)}
              style={{ width: 56 }}
            />
            <input
              type="text"
              className="form-control"
              value={localBg}
              onChange={(e) => setLocalBg(e.target.value)}
              placeholder="#ffffff"
            />
          </div>
        </div>

        <div className="mb-3">
          <EmailImageUpload
            initialImage={localImage}
            onImageUpload={setLocalImage}
            allowRegenerate={true}
          />
          <small className="text-muted">
            API may send image in `image_placeholder`. Uploading will save to `image`.
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            rows={4}
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Optional text below the image"
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW
  return (
    <div
      className="email-component mb-3"
      style={{
        backgroundColor: localBg,
        padding: "28px 16px",
        borderRadius: 8,
      }}
    >
      {!previewMode && (
        <div className="component-controls mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <StyleControls 
            id={id} 
            componentName={component_name}
            styleSettings={styleSettings}
            previewMode={previewMode}
          />
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          style={{
            width: 280,
            height: 280,
            borderRadius: "50%",
            overflow: "hidden",
            backgroundColor: "#f3f3f3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Circular"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <img
              src="/img-preview.svg"
              alt="Placeholder"
              style={{ maxHeight: 90, opacity: 0.6 }}
            />
          )}
        </div>
      </div>

      {!!localDescription && (
        <div
          style={{
            marginTop: 16,
            textAlign: "center",
            color: effectiveStyleSettings?.primaryColor || "#333333",
            fontFamily,
            lineHeight: 1.6,
            fontSize: 16,
            maxWidth: 760,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {localDescription}
        </div>
      )}
    </div>
  );
};

export default ImgCircle;


